import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { useEffect, useRef, useState } from "react";
import {
  listFetchErrorKey,
  listFetchStateKey,
  listRowKey,
  listRows,
} from "../../../shared/connectors/listRows";
import { connectorAllowedModels } from "../../../shared/connectors/models";
import type { ListField } from "../../../shared/connectors/types";
import { sharedMutators } from "../../../shared/mutators";
import { Box } from "../Box";
import { Button } from "../Button";
import { ClickButton } from "../ClickButton";
import { Icon } from "../Icon";
import { InlineInputButton } from "../InlineInput";
import { Input } from "../Input";
import { ListTable } from "../ListTable";
import { HoverActions, ListBox } from "../Settings/shared";
import { Text } from "../Text";
import type { App } from "./Form";

const InputRow = styled("div")`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
`;

const HelperRow = styled("div")`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  padding: 6px 8px;
`;

const FetchLink = styled(ClickButton)`
  font-weight: 350;
  font-size: 10px;
  line-height: 12px;
  letter-spacing: -1%;
  color: var(--text-primary);
  opacity: 0.3;
  white-space: nowrap;
  flex-shrink: 0;
  &:hover {
    opacity: 0.8;
  }
`;

const AddButton = styled(InlineInputButton)`
  background: var(--alpha4);
  &:hover:not(:disabled) {
    background: var(--alpha8);
  }
`;

type Props = {
  item: ListField;
  app: App;
  disabled?: boolean;
  onEdit?: () => void;
};

export function ModelsListField({ item, app, disabled, onEdit }: Props) {
  const z = useZero();
  const column = item.columns[0]!;
  const rows = listRows(app.configuration, item.prefix);
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // A list has no native validity, so anchor the "at least one row" rule on the
  // add-input, which the modal's reportValidity() gate already flushes on save.
  useEffect(() => {
    inputRef.current?.setCustomValidity(
      item.required && rows.length === 0 ? "Add at least one entry." : ""
    );
  }, [item.required, rows.length]);

  const base = () => ({
    connectedAppId: app.id,
    name: app.name,
    identifier: app.identifier,
    urlPattern: app.urlPattern,
  });

  const addModel = (raw: string): boolean => {
    const value = raw.trim();
    if (!value) return false;
    if (connectorAllowedModels(app.configuration).includes(value)) return false;
    const nextId = rows.length ? Math.max(...rows.map((row) => row.id)) + 1 : 0;
    onEdit?.();
    z.mutate(
      sharedMutators.updateConnectedApp({
        ...base(),
        configuration: {
          ...app.configuration,
          [listRowKey(item.prefix, nextId, column.field)]: value,
        },
      })
    );
    return true;
  };

  const tryAdd = () => {
    if (addModel(draft)) setDraft("");
  };

  const removeModel = (id: number) => {
    onEdit?.();
    const rowPrefix = `${item.prefix}.${id}.`;
    z.mutate(
      sharedMutators.updateConnectedApp({
        ...base(),
        configuration: Object.fromEntries(
          Object.entries(app.configuration).filter(
            ([key]) => !key.startsWith(rowPrefix)
          )
        ),
      })
    );
  };

  const fetchState = app.configuration[listFetchStateKey(item.prefix)];
  const fetching = fetchState === "fetching";
  const fetchError =
    fetchState === "error"
      ? app.configuration[listFetchErrorKey(item.prefix)]
      : null;

  return (
    <div>
      <Box horizontal={8} vertical={10}>
        <Text variant="text-small-md" color="var(--text-primary)">
          {item.label}
        </Text>
      </Box>

      {!disabled && (
        <InputRow>
          <Input
            ref={inputRef}
            type="text"
            placeholder={
              column.placeholder
                ? `Add a model ID (e.g. ${column.placeholder})`
                : "Add a model ID"
            }
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                tryAdd();
              }
            }}
            style={{ width: "100%", paddingRight: 36 }}
          />
          <AddButton
            type="button"
            disabled={!draft.trim()}
            aria-label="Add model"
            onClick={tryAdd}
          >
            <Icon name="plus" size={16} />
          </AddButton>
        </InputRow>
      )}

      <HelperRow>
        <Text
          variant="text-small"
          color={fetchError ? "var(--red-600)" : "var(--text-secondary)"}
        >
          {fetchError ?? item.helperText}
        </Text>
        {item.fetch && !disabled && (
          <FetchLink
            disabled={fetching}
            onClick={() => {
              onEdit?.();
              z.mutate(
                sharedMutators.refreshConnectorList({
                  connectedAppId: app.id,
                  prefix: item.prefix,
                })
              );
            }}
          >
            {fetching ? "Fetching…" : item.fetch.label}
          </FetchLink>
        )}
      </HelperRow>

      {rows.length > 0 && (
        <Box top={8}>
          <ListBox
            data-compact="true"
            style={{ flex: "none", maxHeight: 320, overflow: "auto" }}
          >
            <ListTable
              headers={{ name: "Model", actions: "" }}
              columns={{ name: "flex" }}
              items={rows.map((row) => ({
                key: String(row.id),
                name: (
                  <Text
                    variant="text-regular"
                    color="var(--text-primary)"
                    ellipsis
                  >
                    {row.values[column.field]}
                  </Text>
                ),
                actions: (hasMouse: boolean) =>
                  disabled ? null : (
                    <HoverActions visible={hasMouse}>
                      <Button
                        type="button"
                        variant="secondary"
                        icon="x"
                        onClick={() => removeModel(row.id)}
                      />
                    </HoverActions>
                  ),
              }))}
            />
          </ListBox>
          <Box horizontal={8} top={6}>
            <Text variant="text-small" color="var(--text-secondary)">
              Only these models can be used with this connector.
            </Text>
          </Box>
        </Box>
      )}
    </div>
  );
}
